import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  CHECK_TARGET_QUESTION_REQUIREMENTS_URL,
  CREATE_ENTITY_CONFIG_URL,
  EVALUATE_COMPLIANCE_URL,
  EVALUATE_ENTITY_CONFIG_COMPLIANCE_URL,
  GET_COMPLIANCE_ANSWER_HISTORY_URL,
  GET_ENTITY_COMPLIANCE_COUNTDOWN_URL,
  GET_ENTITY_COMPLIANCE_OVERVIEW_URL,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  GET_RULE_CONDITIONS_URL,
  GET_RULE_QUESTIONS_ANSWERS_URL,
  GET_RULE_RESULT_ACTIONS_URL,
  START_COMPLIANCE_COUNTDOWN_URL,
  START_ONGOING_COMPLIANCE_URL,
  SYNC_APPLICATION_ANSWERS_TO_COMPLIANCE_URL,
  VIEW_ENTITY_TYPE_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';

export default {
  name: 'EntityConfigDetail',
  mixins: [alert_mixin],
  data: () => ({
    loading: false,
    entity_config: {},
    entity_type: {},
    form_bindings: [],
    workflow_bindings: [],
    rule_bindings: [],
    risk_bindings: [],
    document_bindings: [],
    document_group_bindings: [],
    notification_bindings: [],
    template_bindings: [],
    verification_service_bindings: [],
    manual_verification_bindings: [],
    child_requirements: [],
    version_history: [],
    compliance_loading: false,
    compliance_rules_detail: [],
    selected_rule_id: '',
    runtime_form: {
      entity_profile_id: '',
      application_id: '',
      compliance_field_id: '',
      section_question_id: ''
    },
    runtime_results: {
      overview: null,
      countdown: null,
      stage_gate: null,
      direct_evaluation: null,
      orchestrated_evaluation: null,
      answer_history: null,
      sync: null,
      ongoing: null
    }
  }),
  computed: {
    entity_type_id() {
      return Number(this.$route.params.entityTypeId);
    },
    entity_type_name() {
      return (
        this.entity_type.entity_type_name ||
        this.entity_config.entity_type_name ||
        this.entity_config.entity_type?.entity_type_name ||
        'Entity'
      );
    },
    summary_items() {
      const verificationBindings = [
        ...this.safe_array(this.verification_service_bindings),
        ...this.safe_array(this.manual_verification_bindings)
      ];
      const documentBindings = this.safe_array(this.document_group_bindings).length
        ? this.document_group_bindings
        : this.document_bindings;

      return [
        { label: 'Forms', count: this.form_bindings.length, icon: 'bi-file-earmark-text' },
        { label: 'Workflows', count: this.workflow_bindings.length, icon: 'bi-diagram-3' },
        { label: 'Rules', count: this.rule_bindings.length, icon: 'bi-sliders' },
        { label: 'Risks', count: this.risk_bindings.length, icon: 'bi-exclamation-triangle' },
        { label: 'Templates', count: this.template_bindings.length, icon: 'bi-file-earmark-richtext' },
        { label: 'Documents', count: documentBindings.length, icon: 'bi-file-earmark-check' },
        { label: 'Notifications', count: this.notification_bindings.length, icon: 'bi-bell' },
        { label: 'Verification', count: verificationBindings.length, icon: 'bi-patch-check' },
        { label: 'Children', count: this.child_requirements.length, icon: 'bi-diagram-3-fill' }
      ];
    },
    entity_description() {
      return (
        this.entity_type.entity_type_description ||
        this.entity_type.description ||
        this.entity_config.entity_type_description ||
        this.entity_config.entity_type?.entity_type_description ||
        'Manage the live configuration, bindings, and requirements for this entity.'
      );
    },
    binding_cards() {
      return this.binding_card_configs().map((config) => this.build_binding_card(config));
    },
    formatted_last_modified() {
      const rawDate = this.entity_config.date_updated || this.entity_config.date_created;
      if (!rawDate) return 'Recently created';

      const parsed = new Date(rawDate);
      if (Number.isNaN(parsed.getTime())) return rawDate;

      return parsed.toLocaleString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    recent_activities() {
      const toneMap = {
        form: {
          icon: 'bi-arrow-repeat',
          toneClass: 'activity-tone-primary',
          title: 'Form binding updated'
        },
        workflow: {
          icon: 'bi-lightning-charge',
          toneClass: 'activity-tone-warning',
          title: 'Workflow adjusted'
        },
        rule: {
          icon: 'bi-shield-check',
          toneClass: 'activity-tone-slate',
          title: 'Rule logic modified'
        }
      };

      return (this.version_history || []).slice(0, 3).map((entry) => {
        const tone = toneMap[entry.binding_type] || {
          icon: 'bi-clock-history',
          toneClass: 'activity-tone-slate',
          title: 'Configuration updated'
        };

        return {
          id: entry.id,
          icon: tone.icon,
          toneClass: tone.toneClass,
          title: `${tone.title}${entry.version ? ` v${entry.version}` : ''}`,
          reason: entry.change_reason || 'Configuration changes were recorded.',
          timestamp: this.formatDate(entry.date_created)
        };
      });
    },
    selected_rule_detail() {
      return this.compliance_rules_detail.find(
        (rule) => String(rule.id) === String(this.selected_rule_id)
      ) || this.compliance_rules_detail[0] || null;
    },
    compliance_totals() {
      return this.compliance_rules_detail.reduce(
        (totals, rule) => ({
          fields: totals.fields + this.safe_array(rule.fields).length,
          conditions: totals.conditions + this.safe_array(rule.conditions).length,
          actions: totals.actions + this.safe_array(rule.actions).length
        }),
        { fields: 0, conditions: 0, actions: 0 }
      );
    },
    visible_runtime_results() {
      return Object.entries(this.runtime_results)
        .filter(([, result]) => Boolean(result))
        .map(([key, result]) => ({
          key,
          label: key.split('_').join(' '),
          result
        }));
    }
  },
  mounted() {
    this.load_entity_config_detail();
  },
  methods: {
    parse_response_data(payload) {
      return typeof payload === 'string' ? JSON.parse(payload) : payload;
    },

    safe_array(value) {
      return Array.isArray(value) ? value : [];
    },

    normalize_rule_id(binding) {
      return Number(
        binding?.compliance_rule ||
        binding?.compliance_rule_id ||
        binding?.rule_id ||
        binding?.id
      );
    },

    normalize_rule_name(binding) {
      return this.resolve_binding_value(binding, [
        'compliance_rule_name',
        'rule_name',
        'name',
        'compliance_rule.name',
        'title'
      ]) || `Rule ${this.normalize_rule_id(binding) || ''}`.trim();
    },

    normalize_question_text(item) {
      return this.resolve_binding_value(item, [
        'question_text',
        'question',
        'section_question.question.question_text',
        'compliance_field.section_question.question.question_text',
        'field_name',
        'name'
      ]) || 'Compliance field';
    },

    normalize_field_id(item) {
      return item?.compliance_field_id || item?.compliance_field || item?.id || '';
    },

    normalize_condition_label(condition) {
      const operator = this.resolve_binding_value(condition, [
        'condition_operator.operator',
        'condition_operator',
        'operator'
      ]);
      const value = this.resolve_binding_value(condition, [
        'value',
        'value_max',
        'selected_options'
      ]);
      const type = this.resolve_binding_value(condition, [
        'condition_type.name',
        'condition_type',
        'type'
      ]);

      return [type, operator, value].filter(Boolean).join(' ');
    },

    normalize_action_label(action) {
      const outcome = this.resolve_binding_value(action, [
        'evaluation_outcome.outcome',
        'evaluation_outcome',
        'outcome',
        'outcome_type'
      ]);
      const target = this.resolve_binding_value(action, [
        'move_to_status.status',
        'move_to_status',
        'target_status',
        'action'
      ]);
      return `${outcome || 'Outcome'} -> ${target || 'No target status'}`;
    },

    format_count_label(count, singular, plural) {
      return `${count} ${count === 1 ? singular : plural}`;
    },

    binding_card_configs() {
      const verificationBindings = [
        ...this.safe_array(this.verification_service_bindings),
        ...this.safe_array(this.manual_verification_bindings)
      ];
      const documentBindings = this.safe_array(this.document_group_bindings).length
        ? this.document_group_bindings
        : this.document_bindings;

      return [
        {
          key: 'forms',
          bindings: this.form_bindings,
          icon: 'bi-file-earmark-text-fill',
          titleKeys: ['form_name', 'verification_form_name', 'name', 'title'],
          emptyTitle: 'No forms bound',
          activeDescription: 'Form binding configured for this entity.',
          emptyDescription: 'No forms are currently bound to this entity.',
          singular: 'Form',
          plural: 'Forms',
          actionLabel: 'Manage Forms',
          action: 'go_to_create_form'
        },
        {
          key: 'workflows',
          bindings: this.workflow_bindings,
          icon: 'bi-diagram-3-fill',
          iconClass: 'entity-vibrant-binding-icon-violet',
          titleKeys: ['workflow_name', 'entity_type_workflow_name', 'name', 'title'],
          emptyTitle: 'No workflows bound',
          activeDescription: 'Workflow binding configured for this entity.',
          emptyDescription: 'No workflows are currently bound to this entity.',
          singular: 'Workflow',
          plural: 'Workflows',
          actionLabel: 'Manage Workflows',
          action: 'go_to_create_workflow'
        },
        {
          key: 'rules',
          bindings: this.rule_bindings,
          icon: 'bi-shield-check',
          iconClass: 'entity-vibrant-binding-icon-rose',
          titleKeys: ['rule_name', 'compliance_rule_name', 'name', 'title'],
          emptyTitle: 'No compliance rules bound',
          activeDescription: 'Compliance rule binding configured for this entity.',
          emptyDescription: 'No compliance rules are currently bound to this entity.',
          singular: 'Rule',
          plural: 'Rules',
          actionLabel: 'Manage Rules',
          action: 'go_to_compliance_management'
        },
        {
          key: 'risks',
          bindings: this.risk_bindings,
          icon: 'bi-exclamation-triangle-fill',
          iconClass: 'entity-vibrant-binding-icon-rose',
          titleKeys: [
            'risk_assessment_name',
            'assessment_name',
            'risk_framework_assessment_name',
            'entity_risk_framework_assessment.risk_framework_assessment.risk_assessment',
            'name',
            'title'
          ],
          emptyTitle: 'No risks bound',
          activeDescription: 'Risk binding configured for this entity.',
          emptyDescription: 'No risk assessments are currently bound to this entity.',
          singular: 'Risk',
          plural: 'Risks',
          actionLabel: 'Manage Risks',
          action: 'go_to_bind_risks'
        },
        {
          key: 'templates',
          bindings: this.template_bindings,
          icon: 'bi-file-earmark-richtext-fill',
          titleKeys: ['template_name', 'document_template_name', 'document_name', 'name', 'title'],
          emptyTitle: 'No templates bound',
          activeDescription: 'Template binding configured for this entity.',
          emptyDescription: 'No templates are currently bound to this entity.',
          singular: 'Template',
          plural: 'Templates',
          actionLabel: 'View Templates',
          action: 'go_to_view_template'
        },
        {
          key: 'documents',
          bindings: documentBindings,
          icon: 'bi-file-earmark-check-fill',
          iconClass: 'entity-vibrant-binding-icon-violet',
          titleKeys: ['group_name', 'document_group.name', 'document_name', 'document_type', 'name', 'title'],
          emptyTitle: 'No document groups bound',
          activeDescription: 'Document group binding configured for this entity.',
          emptyDescription: 'No document groups are currently bound to this entity.',
          singular: 'Document Group',
          plural: 'Document Groups',
          actionLabel: 'Manage Documents',
          action: 'go_to_manage_documents'
        },
        {
          key: 'notifications',
          bindings: this.notification_bindings,
          icon: 'bi-bell-fill',
          iconClass: 'entity-vibrant-binding-icon-violet',
          titleKeys: ['notification_name', 'rule_name', 'name', 'title'],
          emptyTitle: 'No notifications bound',
          activeDescription: 'Notification binding configured for this entity.',
          emptyDescription: 'No notification rules are currently bound to this entity.',
          singular: 'Notification',
          plural: 'Notifications',
          actionLabel: 'Manage Notifications',
          action: 'go_to_notification_management'
        },
        {
          key: 'verification',
          bindings: verificationBindings,
          icon: 'bi-patch-check-fill',
          titleKeys: ['verification_service_name', 'service_name', 'verification_type', 'name', 'title'],
          emptyTitle: 'No verification services bound',
          activeDescription: 'Verification binding configured for this entity.',
          emptyDescription: 'No verification services are currently bound to this entity.',
          singular: 'Verification',
          plural: 'Verifications',
          actionLabel: 'Manage Verification',
          action: 'go_to_verification_services'
        },
        {
          key: 'children',
          bindings: this.child_requirements,
          icon: 'bi-diagram-3-fill',
          iconClass: 'entity-vibrant-binding-icon-rose',
          titleKeys: ['display_name', 'child_entity_type_name', 'entity_type_name', 'sub_entity_type_name', 'name'],
          emptyTitle: 'No child entities required',
          activeDescription: 'Child entity requirement configured for this entity.',
          emptyDescription: 'No child entity requirements are currently configured.',
          singular: 'Child Requirement',
          plural: 'Child Requirements',
          actionLabel: 'Manage Children',
          action: 'go_to_manage_sub_entities'
        }
      ];
    },

    resolve_binding_value(source, keys = []) {
      if (!source || typeof source !== 'object') return '';

      for (const key of keys) {
        const parts = key.split('.');
        let value = source;

        for (const part of parts) {
          if (!value || typeof value !== 'object') {
            value = undefined;
            break;
          }

          value = Reflect.get(value, part);
        }

        if (value !== null && value !== undefined && String(value).trim()) {
          return String(value).trim();
        }
      }

      return '';
    },

    build_binding_card({
      key,
      bindings,
      icon,
      iconClass = '',
      titleKeys,
      emptyTitle,
      activeDescription,
      emptyDescription,
      singular,
      plural,
      actionLabel,
      action
    }) {
      const safeBindings = this.safe_array(bindings);
      const count = safeBindings.length;
      const firstBinding = safeBindings[0] || {};
      const title = count
        ? this.resolve_binding_value(firstBinding, titleKeys) || this.format_count_label(count, singular, plural)
        : emptyTitle;
      const description = count
        ? this.resolve_binding_value(firstBinding, [
            'description',
            'binding_description',
            'status',
            'cadence_kind',
            'rule_mode',
            'workflow_status'
          ]) || activeDescription
        : emptyDescription;
      const statusText = count ? this.format_count_label(count, singular, plural) : 'Not Configured';
      const footerText = count
        ? `${this.format_count_label(count, singular.toLowerCase(), plural.toLowerCase())} from backend`
        : `Open ${singular.toLowerCase()} management to configure`;

      return Object.fromEntries([
        ['key', key],
        ['title', title],
        ['description', description],
        ['icon', icon],
        ['iconClass', iconClass],
        ['statusClass', count ? 'entity-status-active' : 'entity-status-review'],
        ['statusText', statusText],
        ['footerText', footerText],
        ['actionLabel', actionLabel],
        ['action', action]
      ]);
    },

    open_binding_card(card) {
      const allowedActions = {
        go_to_create_form: this.go_to_create_form,
        go_to_create_workflow: this.go_to_create_workflow,
        go_to_compliance_management: this.go_to_compliance_management,
        go_to_view_template: this.go_to_view_template,
        go_to_manage_documents: this.go_to_manage_documents,
        go_to_notification_management: this.go_to_notification_management,
        go_to_verification_services: this.go_to_verification_services,
        go_to_manage_sub_entities: this.go_to_manage_sub_entities,
        go_to_bind_risks: this.go_to_bind_risks
      };
      const action = card?.action ? Reflect.get(allowedActions, card.action) : null;

      if (typeof action !== 'function') return;

      action.call(this);
    },

    formatDate(value) {
      if (!value) return 'Recently';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;

      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    go_to_bind_subsystems() {
      this.$router.push({
        name: 'EntitySubsystemBindings',
        params: {
          entityTypeId: this.entity_type_id
        }
      });
    },
    go_to_bind_risks() {
      this.$router.push({
        name: 'EntitySubsystemBindings',
        params: {
          entityTypeId: this.entity_type_id
        },
        query: {
          tab: 'risk'
        }
      });
    },
    build_subsystem_route(route_name) {
      const query = Object.fromEntries([
        ['entityTypeId', this.entity_type_id ? String(this.entity_type_id) : ''],
        ['entityTypeName', this.entity_type_name]
      ]);

      return Object.fromEntries([
        ['name', route_name],
        ['query', query]
      ]);
    },
    go_to_create_form() {
      this.$router.push(this.build_subsystem_route('CreateForm'));
    },
    go_to_create_workflow() {
      this.$router.push(this.build_subsystem_route('CreateWorkflow'));
    },
    go_to_compliance_management() {
      this.$router.push(this.build_subsystem_route('ComplianceManagement'));
    },
    go_to_view_template() {
      this.$router.push(this.build_subsystem_route('ViewTemplate'));
    },
    go_to_manage_documents() {
      this.$router.push(this.build_subsystem_route('ManageDocuments'));
    },
    go_to_notification_management() {
      this.$router.push(this.build_subsystem_route('NotificationManagement'));
    },
    go_to_verification_services() {
      this.$router.push(this.build_subsystem_route('VerificationServices'));
    },
    go_to_manage_sub_entities() {
      this.$router.push(this.build_subsystem_route('ManageSubEntities'));
    },

    async fetch_entity_type() {
      const response = await axios.get(
        VIEW_ENTITY_TYPE_URL,
        get_request_config({
          params: {
            entity_type_id: this.entity_type_id
          }
        })
      );
      const responseData = this.parse_response_data(response.data);
      const items = responseData.data || [];
      this.entity_type = Array.isArray(items)
        ? (items.find((item) => Number(item.id) === this.entity_type_id) || items[0] || {})
        : items;
    },

    async fetch_full_config() {
      const response = await axios.get(
        GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
        get_request_config({
          params: {
            entity_type_id: this.entity_type_id
          }
        })
      );
      const responseData = this.parse_response_data(response.data);
      return responseData.data || {};
    },

    async ensure_entity_config_exists() {
      return this.fetch_full_config().catch(async (error) => {
        if (error?.response?.status !== 404) {
          throw error;
        }

        await axios.post(
          CREATE_ENTITY_CONFIG_URL,
          {
            entity_type_id: this.entity_type_id
          },
          get_request_config()
        );

        return this.fetch_full_config();
      });
    },

    async load_entity_config_detail() {
      this.loading = true;
      const loader = await this.show_loader('Loading entity config...');

      try {
        await this.fetch_entity_type();
        const configData = await this.ensure_entity_config_exists();

        this.entity_config = configData.entity_config || {};
        this.form_bindings = this.safe_array(configData.form_bindings);
        this.workflow_bindings = this.safe_array(configData.workflow_bindings);
        this.rule_bindings = this.safe_array(configData.rule_bindings);
        this.risk_bindings = this.safe_array(configData.risk_bindings);
        this.document_bindings = this.safe_array(configData.document_bindings);
        this.document_group_bindings = this.safe_array(configData.document_group_bindings);
        this.notification_bindings = this.safe_array(configData.notification_bindings);
        this.template_bindings = this.safe_array(configData.template_bindings);
        this.verification_service_bindings = this.safe_array(configData.verification_service_bindings);
        this.manual_verification_bindings = this.safe_array(configData.manual_verification_bindings);
        this.child_requirements = this.safe_array(configData.child_requirements);
        this.version_history = this.safe_array(configData.version_history);
        await this.load_compliance_workspace();
      } catch (error) {
        const responseData = this.parse_response_data(error?.response?.data || {});
        this.show_error(responseData.message || 'Failed to load entity configuration.');
      } finally {
        await this.close_loader(loader);
        this.loading = false;
      }
    },

    async load_compliance_workspace() {
      this.compliance_loading = true;
      try {
        const activeBindings = this.safe_array(this.rule_bindings)
          .map((binding) => ({
            binding,
            id: this.normalize_rule_id(binding),
            name: this.normalize_rule_name(binding)
          }))
          .filter((rule) => Number.isFinite(rule.id));

        const details = await Promise.all(activeBindings.map((rule) => this.fetch_rule_workspace(rule)));
        this.compliance_rules_detail = details;

        if (!this.selected_rule_id && details.length) {
          this.selected_rule_id = String(details[0].id);
        }
      } catch (error) {
        const responseData = this.parse_response_data(error?.response?.data || {});
        this.show_error(responseData.message || 'Failed to load compliance workspace.');
      } finally {
        this.compliance_loading = false;
      }
    },

    async fetch_rule_workspace(rule) {
      const [fieldsResponse, conditionsResponse, actionsResponse] = await Promise.all([
        axios.get(
          GET_RULE_QUESTIONS_ANSWERS_URL,
          get_request_config({ params: { compliance_rule_id: rule.id, entity_profile_id: this.runtime_form.entity_profile_id || 0 } })
        ).catch((error) => error.response),
        axios.get(GET_RULE_CONDITIONS_URL, get_request_config({ params: { rule_id: rule.id } })).catch((error) => error.response),
        axios.get(GET_RULE_RESULT_ACTIONS_URL, get_request_config({ params: { rule_id: rule.id } })).catch((error) => error.response)
      ]);

      const fieldsPayload = this.parse_response_data(fieldsResponse?.data || {});
      const conditionsPayload = this.parse_response_data(conditionsResponse?.data || {});
      const actionsPayload = this.parse_response_data(actionsResponse?.data || {});

      const workspace = {
        id: rule.id,
        name: rule.name,
        binding: rule.binding,
        fields: this.safe_array(fieldsPayload.data?.questions || fieldsPayload.questions || fieldsPayload.results || fieldsPayload.data),
        conditions: this.safe_array(conditionsPayload.conditions || conditionsPayload.data),
        actions: this.safe_array(actionsPayload.actions || actionsPayload.data)
      };

      return workspace;
    },

    ensure_runtime_entity() {
      if (!this.runtime_form.entity_profile_id) {
        this.show_error('Enter an entity profile ID first.');
        return false;
      }
      return true;
    },

    async run_runtime_action(action_name, callback) {
      if (!this.ensure_runtime_entity()) return;
      const loader = await this.show_loader(`${action_name}...`);
      try {
        await callback();
      } catch (error) {
        const responseData = this.parse_response_data(error?.response?.data || {});
        this.show_error(responseData.message || `${action_name} failed.`);
      } finally {
        await this.close_loader(loader);
      }
    },

    async sync_onboarding_answers() {
      await this.run_runtime_action('Syncing onboarding answers', async () => {
        const response = await axios.post(
          SYNC_APPLICATION_ANSWERS_TO_COMPLIANCE_URL,
          { entity_profile_id: this.runtime_form.entity_profile_id },
          get_request_config()
        );
        this.runtime_results.sync = this.parse_response_data(response.data);
        await this.load_compliance_workspace();
      });
    },

    async load_entity_compliance_overview() {
      await this.run_runtime_action('Loading compliance overview', async () => {
        const response = await axios.get(
          GET_ENTITY_COMPLIANCE_OVERVIEW_URL,
          get_request_config({ params: { entity_profile_id: this.runtime_form.entity_profile_id } })
        );
        this.runtime_results.overview = this.parse_response_data(response.data);
      });
    },

    async load_entity_countdown() {
      await this.run_runtime_action('Loading compliance countdown', async () => {
        const response = await axios.get(
          GET_ENTITY_COMPLIANCE_COUNTDOWN_URL,
          get_request_config({ params: { entity_profile_id: this.runtime_form.entity_profile_id } })
        );
        this.runtime_results.countdown = this.parse_response_data(response.data);
      });
    },

    async start_countdown() {
      await this.run_runtime_action('Starting compliance countdown', async () => {
        const response = await axios.post(
          START_COMPLIANCE_COUNTDOWN_URL,
          {
            entity_profile_id: this.runtime_form.entity_profile_id,
            compliance_rule_ids: this.selected_rule_detail ? [this.selected_rule_detail.id] : undefined
          },
          get_request_config()
        );
        this.runtime_results.countdown = this.parse_response_data(response.data);
      });
    },

    async start_ongoing_compliance() {
      await this.run_runtime_action('Starting ongoing compliance', async () => {
        const response = await axios.post(
          START_ONGOING_COMPLIANCE_URL,
          {
            entity_profile_id: this.runtime_form.entity_profile_id,
            compliance_rule_id: this.selected_rule_detail?.id
          },
          get_request_config()
        );
        this.runtime_results.ongoing = this.parse_response_data(response.data);
      });
    },

    async check_stage_gate() {
      if (!this.runtime_form.section_question_id) {
        this.show_error('Enter a section question ID to check answered/verified requirements.');
        return;
      }
      await this.run_runtime_action('Checking required field', async () => {
        const response = await axios.post(
          CHECK_TARGET_QUESTION_REQUIREMENTS_URL,
          {
            target_question_id: this.runtime_form.section_question_id,
            entity_profile_id: this.runtime_form.entity_profile_id
          },
          get_request_config()
        );
        this.runtime_results.stage_gate = this.parse_response_data(response.data);
      });
    },

    async evaluate_direct_compliance() {
      await this.run_runtime_action('Evaluating compliance', async () => {
        const response = await axios.post(
          EVALUATE_COMPLIANCE_URL,
          { entity_profile_id: this.runtime_form.entity_profile_id },
          get_request_config()
        );
        this.runtime_results.direct_evaluation = this.parse_response_data(response.data);
      });
    },

    async evaluate_orchestrated_compliance() {
      if (!this.runtime_form.application_id) {
        this.show_error('Enter an application ID for orchestrated compliance.');
        return;
      }
      await this.run_runtime_action('Running orchestrated compliance', async () => {
        const response = await axios.post(
          EVALUATE_ENTITY_CONFIG_COMPLIANCE_URL,
          {
            entity_type_id: this.entity_type_id,
            entity_profile_id: this.runtime_form.entity_profile_id,
            application_id: this.runtime_form.application_id
          },
          get_request_config()
        );
        this.runtime_results.orchestrated_evaluation = this.parse_response_data(response.data);
      });
    },

    async load_answer_history() {
      if (!this.runtime_form.compliance_field_id) {
        this.show_error('Enter a compliance field ID first.');
        return;
      }
      await this.run_runtime_action('Loading answer history', async () => {
        const response = await axios.get(
          GET_COMPLIANCE_ANSWER_HISTORY_URL,
          get_request_config({
            params: {
              compliance_field_id: this.runtime_form.compliance_field_id,
              entity_profile_id: this.runtime_form.entity_profile_id
            }
          })
        );
        this.runtime_results.answer_history = this.parse_response_data(response.data);
      });
    }
  }
};
